import { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import Box from '@mui/material/Box';

export default function MouseBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Mouse position with spring physics for smooth following
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animation
  const springConfig = { damping: 25, stiffness: 150 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Normalize mouse position to -1 to 1
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      
      mouseX.set(x);
      mouseY.set(y);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(135deg, #0A0A0F 0%, #0D0D14 50%, #0A0A0F 100%)',
      }}
    >
      {/* Primary gradient orb - follows mouse */}
      <motion.div
        style={{
          position: 'absolute',
          width: '60vw',
          height: '60vw',
          maxWidth: '800px',
          maxHeight: '800px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(68, 212, 130, 0.15) 0%, rgba(68, 212, 130, 0.05) 40%, transparent 70%)',
          filter: 'blur(60px)',
          x: smoothX,
          y: smoothY,
          translateX: '-50%',
          translateY: '-50%',
          left: '50%',
          top: '50%',
        }}
      />

      {/* Secondary orb - opposite movement */}
      <motion.div
        style={{
          position: 'absolute',
          width: '40vw',
          height: '40vw',
          maxWidth: '600px',
          maxHeight: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(68, 212, 130, 0.1) 0%, rgba(46, 186, 107, 0.03) 50%, transparent 70%)',
          filter: 'blur(80px)',
          left: '70%',
          top: '20%',
        }}
        animate={{
          x: [0, 50, -30, 0],
          y: [0, -30, 50, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Tertiary orb - slow floating */}
      <motion.div
        style={{
          position: 'absolute',
          width: '50vw',
          height: '50vw',
          maxWidth: '700px',
          maxHeight: '700px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(68, 212, 130, 0.08) 0%, transparent 60%)',
          filter: 'blur(100px)',
          left: '20%',
          top: '60%',
        }}
        animate={{
          x: [-50, 50, -50],
          y: [50, -50, 50],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Grid pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.03,
          backgroundImage: `
            linear-gradient(rgba(68, 212, 130, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(68, 212, 130, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
        }}
      />

      {/* Radial gradient from center */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%',
          height: '150%',
          background: 'radial-gradient(ellipse at center, transparent 0%, #0A0A0F 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Top vignette */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '30%',
          background: 'linear-gradient(180deg, #0A0A0F 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Bottom vignette */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '30%',
          background: 'linear-gradient(0deg, #0A0A0F 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}
